import { FC } from "react";

import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";

import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { zeffyInfoboxClasses } from "@/dashboard-design-system/Infobox/types";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Idea } from "@/icons/outlined";

interface SuggestionBannerProps {
  lead: string;
  description: string;
  acceptLabel: string;
  dismissLabel: string;
  onAccept: () => void;
  onDismiss: () => void;
  outlineDismiss?: boolean;
}

export const SuggestionBanner: FC<SuggestionBannerProps> = ({
  lead,
  description,
  acceptLabel,
  dismissLabel,
  onAccept,
  onDismiss,
  outlineDismiss = false,
}) => {
  return (
    <Collapse in appear timeout="auto">
      <DashboardInfobox
        vibe="brand"
        size="medium"
        icon={<Idea />}
        data-test="rate-setting-suggestion"
        sx={
          outlineDismiss
            ? (theme) => ({
                [`& .${zeffyInfoboxClasses.paper}`]: { p: 1.5 },
                [`& .${zeffyInfoboxClasses.icon}`]: {
                  width: theme.spacing(2.5),
                  height: theme.spacing(2.5),
                  mr: 1,
                  mt: 0.125,
                },
                [`& .${zeffyInfoboxClasses.contentPaddingTop}`]: { pt: 0 },
              })
            : undefined
        }
        customButton={
          outlineDismiss ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={1}
              justifyContent="flex-end"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <DashboardButton
                size="small"
                variant="outlined"
                vibe="brand"
                sx={(theme) => ({ lineHeight: theme.typography.body2.lineHeight })}
                onClick={onDismiss}
              >
                {dismissLabel}
              </DashboardButton>
              <DashboardButton
                size="small"
                variant="tonal"
                vibe="brand"
                sx={(theme) => ({ lineHeight: theme.typography.body2.lineHeight })}
                onClick={onAccept}
              >
                {acceptLabel}
              </DashboardButton>
            </Stack>
          ) : undefined
        }
        buttonsProps={
          outlineDismiss
            ? undefined
            : [
                {
                  key: "dismiss",
                  variant: outlineDismiss ? "outlined" : "text",
                  children: dismissLabel,
                  onClick: onDismiss,
                },
                { key: "accept", variant: "filled", children: acceptLabel, onClick: onAccept },
              ]
        }
      >
        <DashboardTypography
          component="span"
          variant="body2"
          sx={{ fontWeight: 700 }}
          vibe={outlineDismiss ? "text-brand-moderate" : undefined}
        >
          {lead}
        </DashboardTypography>{" "}
        {description}
      </DashboardInfobox>
    </Collapse>
  );
};
